/**
 * EmailIntakeTemplatePage — 邮件收单模板管理
 *
 * Manages email intake parsing templates per carrier / shipping line.
 * Templates define extraction patterns for booking emails (export/import).
 * Stored in document_templates with doc_type='email_intake'.
 *
 * Each template configures:
 *   - carrier name + direction (import/export/both)
 *   - subject pattern (regex to match email subjects)
 *   - field extraction rules (named lines, regex patterns, section markers)
 *   - required vs optional fields
 *   - default values
 *
 * API: existing document-templates CRUD with docType='email_intake'
 */

import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Mail,
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  Loader2,
  RefreshCw,
  Ship,
  Star,
  Copy,
  AlertTriangle,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────

interface FieldRule {
  field: string;          // e.g. 'bookingRef', 'containerNo', 'vessel', 'voyage', 'eta'
  labels: string[];       // Named line labels: ['booking ref', 'booking no', 'bkg ref']
  patterns: string[];     // Regex patterns to try: ['/BKG[:\\s]+([A-Z0-9]{6,20})/i']
  required: boolean;
  defaultValue?: string;
}

interface IntakeTemplate {
  carrier: string;                     // e.g. 'OOCL', 'Evergreen', 'Generic Export'
  direction: 'import' | 'export' | 'both';
  subjectPatterns: string[];           // Regex patterns to match email subjects
  senderPatterns: string[];            // Patterns to match sender email addresses
  fieldRules: FieldRule[];
  containerOptional: boolean;          // If true, container not required (export with booking ref)
  notes: string;                       // Human-readable notes
}

interface DocTemplate {
  id: string;
  tenant_id: string;
  doc_type: string;
  name: string;
  description: string | null;
  is_default: boolean;
  html_template: string;               // JSON stringified IntakeTemplate
  variables: string;                    // JSON stringified defaults
  version: number;
  created_at: string;
  updated_at: string;
}

// ─── Default Template ──────────────────────────────

const DEFAULT_FIELD_RULES: FieldRule[] = [
  { field: 'bookingRef', labels: ['booking ref', 'booking reference', 'booking no', 'bkg ref', 'bkg no', 'carrier booking'], patterns: ['/BKG[:\\\\s]+([A-Z0-9]{6,20})/i', '/BOOKING[:\\\\s#]+([A-Z0-9]{6,20})/i'], required: false },
  { field: 'containerNo', labels: ['container', 'container no', 'cntr no', 'cntr'], patterns: ['/([A-Z]{4}\\\\d{7})/'], required: true },
  { field: 'blNumber', labels: ['bl', 'bl no', 'b/l', 'bill of lading'], patterns: ['/\\\\b([A-Z]{4}[A-Z0-9]{8,12})\\\\b/'], required: false },
  { field: 'vessel', labels: ['vessel', 'vessel name', 'vsl'], patterns: [], required: false },
  { field: 'voyage', labels: ['voyage', 'voyage no', 'voy'], patterns: [], required: false },
  { field: 'eta', labels: ['eta', 'estimated arrival', 'arrival date', 'etd', 'estimated departure'], patterns: [], required: false },
  { field: 'pol', labels: ['pol', 'port of loading', 'load port'], patterns: [], required: false },
  { field: 'pod', labels: ['pod', 'port of discharge', 'discharge port'], patterns: [], required: false },
  { field: 'weight', labels: ['weight', 'gross weight', 'cargo weight'], patterns: ['/([\\\\d,.]+)\\\\s*(kg|kgs|mt|ton)/i'], required: false },
  { field: 'commodity', labels: ['commodity', 'cargo', 'goods description'], patterns: [], required: false },
];

function createEmptyTemplate(): IntakeTemplate {
  return {
    carrier: '',
    direction: 'export',
    subjectPatterns: [],
    senderPatterns: [],
    fieldRules: JSON.parse(JSON.stringify(DEFAULT_FIELD_RULES)),
    containerOptional: true,
    notes: '',
  };
}

function parseTemplate(htmlTemplate: string): IntakeTemplate {
  try {
    return JSON.parse(htmlTemplate);
  } catch {
    return createEmptyTemplate();
  }
}

// ─── Component ─────────────────────────────────────

export default function EmailIntakeTemplatePage() {
  const [templates, setTemplates] = useState<DocTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit panel
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsDefault, setEditIsDefault] = useState(false);
  const [editTemplate, setEditTemplate] = useState<IntakeTemplate>(createEmptyTemplate());

  // New dialog
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<DocTemplate | null>(null);

  // ─── Load ────────────────────────────────────────

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ data: DocTemplate[]; total: number }>('/document-templates?docType=email_intake&limit=100');
      setTemplates(res?.data ?? (Array.isArray(res) ? res as unknown as DocTemplate[] : []));
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // ─── Create ──────────────────────────────────────

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const tpl = createEmptyTemplate();
      await api.post('/document-templates', {
        docType: 'email_intake',
        name: newName.trim(),
        description: `${newName.trim()} 邮件收单解析模板`,
        htmlTemplate: JSON.stringify(tpl, null, 2),
        variables: JSON.stringify(DEFAULT_FIELD_RULES.map(r => r.field)),
        isDefault: false,
      });
      setNewName('');
      setShowNew(false);
      await loadTemplates();
    } finally {
      setSaving(false);
    }
  };

  // ─── Edit ────────────────────────────────────────

  const openEdit = (t: DocTemplate) => {
    setEditingId(t.id);
    setEditName(t.name);
    setEditDescription(t.description ?? '');
    setEditIsDefault(t.is_default);
    setEditTemplate(parseTemplate(t.html_template));
  };

  const handleSave = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      await api.patch(`/document-templates/${editingId}`, {
        name: editName,
        description: editDescription,
        isDefault: editIsDefault,
        htmlTemplate: JSON.stringify(editTemplate, null, 2),
        variables: JSON.stringify(editTemplate.fieldRules.map(r => r.field)),
      });
      setEditingId(null);
      await loadTemplates();
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete ──────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await api.delete(`/document-templates/${deleteTarget.id}`);
      setDeleteTarget(null);
      await loadTemplates();
    } finally {
      setSaving(false);
    }
  };

  // ─── Duplicate ───────────────────────────────────

  const handleDuplicate = async (t: DocTemplate) => {
    setSaving(true);
    try {
      await api.post('/document-templates', {
        docType: 'email_intake',
        name: `${t.name} (副本)`,
        description: t.description,
        htmlTemplate: t.html_template,
        variables: t.variables,
        isDefault: false,
      });
      await loadTemplates();
    } finally {
      setSaving(false);
    }
  };

  // ─── Field Rules Editor ──────────────────────────

  const updateFieldRule = (idx: number, patch: Partial<FieldRule>) => {
    setEditTemplate(prev => {
      const rules = [...prev.fieldRules];
      rules[idx] = { ...rules[idx], ...patch } as FieldRule;
      return { ...prev, fieldRules: rules };
    });
  };

  const addFieldRule = () => {
    setEditTemplate(prev => ({
      ...prev,
      fieldRules: [...prev.fieldRules, { field: '', labels: [], patterns: [], required: false }],
    }));
  };

  const removeFieldRule = (idx: number) => {
    setEditTemplate(prev => ({
      ...prev,
      fieldRules: prev.fieldRules.filter((_, i) => i !== idx),
    }));
  };

  // ─── Render ──────────────────────────────────────

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">邮件收单模板</h1>
          <p className="text-sm text-muted-foreground">管理不同船公司 / 邮件格式的解析规则，用于自动建 Job</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadTemplates}>
            <RefreshCw className="mr-1 h-4 w-4" />刷新
          </Button>
          <Button size="sm" onClick={() => setShowNew(true)}>
            <Plus className="mr-1 h-4 w-4" />新增模板
          </Button>
        </div>
      </div>

      {/* Template List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-16 text-muted-foreground">
            <Mail className="mb-2 h-10 w-10" />
            <p>暂无邮件收单模板</p>
            <p className="text-xs">点击"新增模板"添加船公司解析规则</p>
            <Button className="mt-4" variant="outline" size="sm" onClick={() => setShowNew(true)}>
              <Plus className="mr-1 h-4 w-4" />新增模板
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => {
            const tpl = parseTemplate(t.html_template);
            return (
              <Card key={t.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openEdit(t)}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Ship className="h-4 w-4 text-blue-500" />
                      {t.name}
                    </CardTitle>
                    <div className="flex items-center gap-1">
                      {t.is_default && <Star className="h-4 w-4 fill-amber-400 text-amber-400" />}
                      <Badge variant={tpl.direction === 'export' ? 'default' : tpl.direction === 'import' ? 'secondary' : 'outline'}>
                        {tpl.direction === 'export' ? '出口' : tpl.direction === 'import' ? '进口' : '通用'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  <p>{t.description ?? '—'}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {tpl.carrier && <Badge variant="outline" className="text-xs">{tpl.carrier}</Badge>}
                    <Badge variant="outline" className="text-xs">{tpl.fieldRules.length} 字段规则</Badge>
                    {tpl.containerOptional && <Badge variant="outline" className="text-xs">柜号可选</Badge>}
                    {tpl.subjectPatterns.length > 0 && (
                      <Badge variant="outline" className="text-xs">{tpl.subjectPatterns.length} 主题匹配</Badge>
                    )}
                  </div>
                  <p className="mt-2 text-xs">v{t.version} · {new Date(t.updated_at).toLocaleDateString('zh-CN')}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ─── Edit Sheet ─────────────────────────────── */}
      <Sheet open={!!editingId} onOpenChange={(open) => { if (!open) setEditingId(null); }}>
        <SheetContent className="w-full sm:max-w-[1000px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Edit className="h-4 w-4" />
              编辑邮件模板
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-col gap-4 py-4">
            {/* Basic Info */}
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>模板名称</Label>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
              </div>
              <div>
                <Label>船公司 / 来源</Label>
                <Input
                  placeholder="例: OOCL, Evergreen"
                  value={editTemplate.carrier}
                  onChange={(e) => setEditTemplate(prev => ({ ...prev, carrier: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <Label>描述</Label>
              <Input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div>
                <Label>方向</Label>
                <select
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  value={editTemplate.direction}
                  onChange={(e) => setEditTemplate(prev => ({ ...prev, direction: e.target.value as IntakeTemplate['direction'] }))}
                >
                  <option value="export">出口</option>
                  <option value="import">进口</option>
                  <option value="both">通用</option>
                </select>
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editTemplate.containerOptional}
                    onChange={(e) => setEditTemplate(prev => ({ ...prev, containerOptional: e.target.checked }))}
                  />
                  柜号可选 (出口预订)
                </label>
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editIsDefault}
                    onChange={(e) => setEditIsDefault(e.target.checked)}
                  />
                  <Star className="h-3 w-3" />设为默认
                </label>
              </div>
            </div>

            {/* Subject & Sender Patterns */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">邮件匹配规则</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3">
                <div>
                  <Label>主题匹配 (每行一个 regex)</Label>
                  <Textarea
                    rows={3}
                    placeholder={'/booking confirmation/i\n/export booking.*OOCL/i'}
                    value={editTemplate.subjectPatterns.join('\n')}
                    onChange={(e) => setEditTemplate(prev => ({
                      ...prev,
                      subjectPatterns: e.target.value.split('\n').filter(s => s.trim()),
                    }))}
                  />
                </div>
                <div>
                  <Label>发件人匹配 (每行一个 regex)</Label>
                  <Textarea
                    rows={2}
                    placeholder={'/@oocl\\.com$/i\n/booking@evergreen/i'}
                    value={editTemplate.senderPatterns.join('\n')}
                    onChange={(e) => setEditTemplate(prev => ({
                      ...prev,
                      senderPatterns: e.target.value.split('\n').filter(s => s.trim()),
                    }))}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Field Rules */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">字段提取规则</CardTitle>
                  <Button variant="outline" size="sm" onClick={addFieldRule}>
                    <Plus className="mr-1 h-3 w-3" />添加字段
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                {editTemplate.fieldRules.map((rule, idx) => (
                  <div key={idx} className="rounded-md border p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Input
                          className="w-32"
                          placeholder="字段名"
                          value={rule.field}
                          onChange={(e) => updateFieldRule(idx, { field: e.target.value })}
                        />
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={rule.required}
                            onChange={(e) => updateFieldRule(idx, { required: e.target.checked })}
                          />
                          必填
                        </label>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeFieldRule(idx)}>
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    </div>
                    <div className="grid gap-2">
                      <div>
                        <Label className="text-xs">标签匹配 (逗号分隔)</Label>
                        <Input
                          className="text-xs"
                          placeholder="booking ref, bkg ref, bkg no"
                          value={rule.labels.join(', ')}
                          onChange={(e) => updateFieldRule(idx, {
                            labels: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                          })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">正则模式 (逗号分隔)</Label>
                        <Input
                          className="text-xs font-mono"
                          placeholder="/BKG[:\\s]+([A-Z0-9]{6,20})/i"
                          value={rule.patterns.join(', ')}
                          onChange={(e) => updateFieldRule(idx, {
                            patterns: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                          })}
                        />
                      </div>
                      {rule.defaultValue !== undefined && (
                        <div>
                          <Label className="text-xs">默认值</Label>
                          <Input
                            className="text-xs"
                            value={rule.defaultValue}
                            onChange={(e) => updateFieldRule(idx, { defaultValue: e.target.value })}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Notes */}
            <div>
              <Label>备注</Label>
              <Textarea
                rows={3}
                placeholder="模板使用说明、特殊注意事项..."
                value={editTemplate.notes}
                onChange={(e) => setEditTemplate(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 border-t pt-4">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
                保存
              </Button>
              <Button variant="outline" onClick={() => {
                const current = templates.find(t => t.id === editingId);
                if (current) handleDuplicate(current);
              }}>
                <Copy className="mr-1 h-4 w-4" />复制
              </Button>
              <div className="flex-1" />
              <Button variant="destructive" size="sm" onClick={() => {
                const current = templates.find(t => t.id === editingId);
                if (current) { setDeleteTarget(current); setEditingId(null); }
              }}>
                <Trash2 className="mr-1 h-4 w-4" />删除
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                <X className="mr-1 h-4 w-4" />关闭
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ─── New Dialog ──────────────────────────────── */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新增邮件收单模板</DialogTitle>
            <DialogDescription>为特定船公司或邮件格式创建解析模板</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label>模板名称</Label>
              <Input
                placeholder="例: OOCL 出口确认, Evergreen 进口通知"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>取消</Button>
            <Button onClick={handleCreate} disabled={!newName.trim() || saving}>
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
              创建
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Delete Dialog ───────────────────────────── */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              确认删除
            </DialogTitle>
            <DialogDescription>
              将删除模板 <strong>{deleteTarget?.name}</strong>。此操作不可撤销。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>取消</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1 h-4 w-4" />}
              确认删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
